import React from 'react';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { EditorToolbarProvider, EditorToolbarUIProvider } from '@atlaskit/editor-common/toolbar';
import { ToolbarSize } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { ToolbarButtonGroup, ToolbarDropdownItemSection, ToolbarSection } from '@atlaskit/editor-toolbar';
import { ToolbarModelRenderer } from '@atlaskit/editor-toolbar-model';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ToolbarInner } from './ToolbarInner';

/**
 * *Warning:* With `platform_editor_toolbar_aifc` enabled this component is no longer used and is replaced with `<ToolbarNext />`.
 *
 * If making changes to this component please ensure to also update `<ToolbarNext />`.
 */
export var Toolbar = function Toolbar(props) {
  return /*#__PURE__*/React.createElement(ToolbarInner, {
    items: props.items,
    editorView: props.editorView,
    editorActions: props.editorActions,
    eventDispatcher: props.eventDispatcher,
    providerFactory: props.providerFactory,
    appearance: props.appearance,
    popupsMountPoint: props.popupsMountPoint,
    popupsBoundariesElement: props.popupsBoundariesElement,
    popupsScrollableElement: props.popupsScrollableElement,
    disabled: props.disabled,
    dispatchAnalyticsEvent: props.dispatchAnalyticsEvent,
    toolbarSize: props.toolbarSize,
    isToolbarReducedSpacing: props.toolbarSize < ToolbarSize.XXL,
    containerElement: props.containerElement
  });
};
var usePluginState = conditionalHooksFactory(function () {
  return expValEquals('platform_editor_toolbar_aifc_patch_3', 'isEnabled', true);
}, function (api) {
  return useSharedPluginStateWithSelector(api, ['connectivity', 'editorViewMode', 'userPreferences'], function (state) {
    var _state$connectivitySt, _state$editorViewMode, _state$userPreference;
    return {
      connectivityStateMode: (_state$connectivitySt = state.connectivityState) === null || _state$connectivitySt === void 0 ? void 0 : _state$connectivitySt.mode,
      editorViewMode: (_state$editorViewMode = state.editorViewModeState) === null || _state$editorViewMode === void 0 ? void 0 : _state$editorViewMode.mode,
      editorToolbarDockingPreference: (_state$userPreference = state.userPreferencesState) === null || _state$userPreference === void 0 || (_state$userPreference = _state$userPreference.preferences) === null || _state$userPreference === void 0 ? void 0 : _state$userPreference.toolbarDockingPosition
    };
  });
}, function (api) {
  var connectivityStateMode = useSharedPluginStateSelector(api, 'connectivity.mode');
  return {
    connectivityStateMode: connectivityStateMode,
    editorViewMode: undefined,
    editorToolbarDockingPreference: undefined
  };
});

/**
 * Renders a primary toolbar, driven by components registed by `editor-plugin-toolbar`. `ToolbarModelRenderer` will just
 * render the toolbar structure, the design is driven per component registered including the toolbar itself.
 *
 * The majority of components UI should use `@atlaskit/editor-toolbar` components.
 */
export var ToolbarNext = function ToolbarNext(_ref) {
  var toolbar = _ref.toolbar,
    components = _ref.components,
    editorView = _ref.editorView,
    editorAPI = _ref.editorAPI,
    popupsMountPoint = _ref.popupsMountPoint,
    editorAppearance = _ref.editorAppearance,
    popupsBoundariesElement = _ref.popupsBoundariesElement,
    popupsScrollableElement = _ref.popupsScrollableElement,
    isDisabled = _ref.isDisabled;
  var _usePluginState = usePluginState(editorAPI),
    connectivityStateMode = _usePluginState.connectivityStateMode,
    editorViewMode = _usePluginState.editorViewMode,
    editorToolbarDockingPreference = _usePluginState.editorToolbarDockingPreference;
  var isOffline = connectivityStateMode === 'offline';
  return /*#__PURE__*/React.createElement(EditorToolbarProvider, {
    editorView: editorView !== null && editorView !== void 0 ? editorView : null,
    editorAppearance: editorAppearance,
    editorViewMode: expValEquals('platform_editor_toolbar_aifc_patch_4', 'isEnabled', true) ? editorViewMode !== null && editorViewMode !== void 0 ? editorViewMode : 'edit' : editorViewMode,
    editorToolbarDockingPreference: editorToolbarDockingPreference
  }, /*#__PURE__*/React.createElement(EditorToolbarUIProvider, {
    api: editorAPI,
    isDisabled: isOffline || isDisabled,
    popupsMountPoint: popupsMountPoint,
    popupsBoundariesElement: popupsBoundariesElement,
    popupsScrollableElement: popupsScrollableElement
    // supress type checks for now
    ,
    fireAnalyticsEvent: expValEquals('platform_editor_toolbar_aifc_toolbar_analytic', 'isEnabled', true) ? function (payload) {
      var _editorAPI$analytics;
      editorAPI === null || editorAPI === void 0 || (_editorAPI$analytics = editorAPI.analytics) === null || _editorAPI$analytics === void 0 || _editorAPI$analytics.actions.fireAnalyticsEvent(payload);
    } : undefined
  }, /*#__PURE__*/React.createElement(ToolbarModelRenderer, {
    toolbar: toolbar,
    components: components,
    fallbacks: {
      group: ToolbarButtonGroup,
      section: ToolbarSection,
      menuSection: ToolbarDropdownItemSection
    }
  })));
};